import React from 'react';
import { gray5, gray60, gray100, red50 } from '../../../../constants/colors';

/**
 * 明細テーブルのセル専用の軽量な入力欄（#3275 検証中）。
 *
 * MUI の OutlinedInput は1個あたり div/input/fieldset/legend/span の
 * 5〜6要素と emotion のスタイル生成を伴う。明細が 122行 × 9列 になると
 * 入力欄だけで 3,800個・DOM 23,000要素規模になり、初回描画でメインスレッドが
 * 止まって「ページが応答しません」に至る。
 *
 * このコンポーネントは素の <input> 1要素だけを描画し、見た目を CSS で再現する。
 * 明細セルが実際に使っている機能（値・エラー枠・読み取り専用・
 * フォーカス/ブラー・数値のカンマ表示）だけに絞っている。
 *
 * バリデーションは呼び出し側の validateArrayField が担っており、
 * ここでは error による見た目の切り替えだけを行う。
 */
export interface PlainCellInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** エラー時は枠線を赤にする */
  error?: boolean;
  /** 計算値採用中など。編集不可にし背景をグレーにする */
  readOnly?: boolean;
  /** 自動入力対象など、背景をグレーにしたい場合 */
  filled?: boolean;
  /** 数値入力（右寄せ・カンマ区切り表示） */
  numeric?: boolean;
  /** 再読取中など、文字を薄く見せたい場合（#3275）。
   *  インラインスタイルは CSS セレクタより優先されるため、
   *  親の sx からは色を上書きできない */
  dimmed?: boolean;
  placeholder?: string;
}

/** 数値文字列にカンマ区切りを付与する（CustomNumberField と同じ規則） */
const formatWithComma = (value: string): string => {
  if (value === '' || value === '-') return value;
  const parts = value.split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? `${intPart}.${parts[1]}` : intPart;
};

/** カンマ区切り文字列から数字・小数点・マイナスのみを抽出する */
const stripNonNumeric = (value: string): string => value.replace(/[^0-9.-]/g, '');

const PlainCellInput: React.FC<PlainCellInputProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  error = false,
  readOnly = false,
  filled = false,
  numeric = false,
  dimmed = false,
  placeholder,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  // 数値欄はフォーカス中だけ生値、それ以外はカンマ区切りで見せる。
  // value には既にカンマ付きの文字列が入っていることがある
  // （明細の初期化が toLocaleString() で整形するため）ので、
  // どちらで来ても同じ表示になるよう一度カンマを外してから組み立てる（#3275）
  const rawValue = numeric ? stripNonNumeric(value) : value;
  const displayValue = numeric ? (isFocused ? rawValue : formatWithComma(rawValue)) : value;

  const borderColor = error ? red50 : isFocused ? gray100 : gray60;
  const backgroundColor = readOnly || filled ? gray5 : '#FFF';

  return (
    <input
      type="text"
      autoComplete="off"
      value={displayValue}
      placeholder={placeholder}
      readOnly={readOnly}
      onChange={(event): void => {
        const next = event.target.value;
        onChange(numeric ? stripNonNumeric(next) : next);
      }}
      onFocus={(): void => {
        setIsFocused(true);
        onFocus?.();
      }}
      onBlur={(): void => {
        setIsFocused(false);
        onBlur?.();
      }}
      style={{
        // OutlinedInput の見た目を素の input で再現する
        width: '100%',
        height: '40px',
        padding: '10px 8px',
        fontSize: '14px',
        fontFamily: 'inherit',
        color: dimmed ? 'rgba(10, 10, 10, 0.3)' : gray100,
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '4px',
        boxSizing: 'border-box',
        outline: 'none',
        textAlign: numeric ? 'right' : 'left',
      }}
    />
  );
};

export default React.memo(PlainCellInput);
